import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, message } from "antd";
import axios from "axios";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import useBusinessMutationInvalidation from "../../hooks/useBusinessMutationInvalidation";
import { useSelection } from "../../SelectionContext";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { genSlug, parseProductNumber } from "./productsShared";
import { companyHasProfile } from "../../utils/activityProfiles";
import {
  buildVariantAttributes,
  comboKey,
} from "../../gestion-produits/VariantManager.jsx";
import ProductsMobile from "./ProductsMobile";
import useProductFormOptions from "../../gestion-produits/useProductFormOptions";
import "./produits-mobile.css";

const API_URL = API_CONFIG.BASE_URL;

const productTypeLabel = (type) => {
  if (type === "raw") return "Matières premières";
  if (type === "production") return "Unité de production";
  return "Produits finis";
};

const productTypeValue = (label) =>
  label === "Matières premières" ? "raw" : "single";

const taxDisplayName = (tax) => {
  if (!tax) return "";
  return `${tax.name} (${Number(tax.rate) || 0} %)`;
};

const firstPackaging = (profiles) => {
  const profile = Array.isArray(profiles) ? profiles[0] : null;
  if (!profile) return undefined;
  return {
    label: profile.label,
    content: Number(profile.contained_quantity) || 0,
    price: Number(profile.reference_purchase_price) || 0,
  };
};

const mapProduct = (product, references, contextWarehouseId) => {
  const tax = references.taxes.find((item) => item.id === product.tax_id);

  return {
    id: product.id,
    backendType: product.product_type,
    isVariantParent: Boolean(product.is_variant_parent),
    parentProductId: product.parent_product_id || null,
    name: product.name || "",
    code: product.item_code || "",
    cat: product.categorie_nom || "Sans catégorie",
    categoryId: product.category_id || null,
    type: productTypeLabel(product.product_type),
    unit: product.unit_short_name || product.unit?.short_name || "",
    unitId: product.unit_id || product.unit?.id || null,
    stock: Number(product.current_stock) || 0,
    alert: Number(product.stock_quantitiy_alert) || 0,
    cost: Number(product.purchase_price) || 0,
    sale: Number(product.sales_price) || 0,
    bc: product.barcode_value || product.item_code || "",
    bcSymbol: product.barcode_symbology || "CODE128",
    wh: product.warehouse_nom || product.main_warehouse_nom || "",
    mainWarehouseId:
      product.main_product_warehouse_id || product.warehouse_id || null,
    contextWarehouseId,
    stockMode: product.stock_management_mode || "standard",
    tax:
      tax?.name ||
      (product.tax_name
        ? taxDisplayName({ name: product.tax_name, rate: product.tax_rate })
        : "Aucune taxe"),
    taxId: product.tax_id || null,
    desc: product.description || "",
    slug: product.slug || "",
    image: product.image || null,
    status: product.status || "actif",
    pk: firstPackaging(product.purchase_packaging_profiles),
  };
};

const getErrorMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.details ||
  error.message ||
  fallback;

const findByName = (items, name) =>
  items.find((item) => item.name === name) || null;

const mobileReferences = (options, selectedWarehouse) => ({
  categories: (options?.categories || []).map((item) => ({ ...item, ico: "tag" })),
  units: (options?.units || []).map((item) => ({ ...item, short: item.short_name })),
  warehouses: (options?.warehouses || []).map((item) => ({ ...item, companyId: item.company_id }))
    .sort((left, right) => Number(String(right.id) === String(selectedWarehouse)) - Number(String(left.id) === String(selectedWarehouse))),
  taxes: [{ id: null, name: "Aucune taxe", rawName: "", v: 0 },
    ...(options?.taxes || []).map((item) => ({ ...item, name: taxDisplayName(item), rawName: item.name, v: Number(item.rate) || 0 }))],
});

export default function MobileProductsPage() {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user, hasPermission } = useAuth();
  const invalidateProducts = useBusinessMutationInvalidation("product");
  const selectedCompanyId = Number(selectedCompany);
  const subscriptionCompanyId = Number(user?.subscription_access?.company_id);
  const subscriptionAccessMatchesSelection =
    !Number.isSafeInteger(selectedCompanyId) ||
    selectedCompanyId <= 0 ||
    !Number.isSafeInteger(subscriptionCompanyId) ||
    subscriptionCompanyId === selectedCompanyId;
  const subscriptionWriteRestricted = Boolean(
    Number(user?.is_superadmin) !== 1 &&
      subscriptionAccessMatchesSelection &&
      user?.subscription_access?.restriction_enforced &&
      ["read_only", "suspended"].includes(
        user?.subscription_access?.access_state,
      ),
  );
  const isVariantsEligible = companyHasProfile(user, "ecommerce");
  const outletContext = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [openCreateSignal, setOpenCreateSignal] = useState(0);
  const formChoices = useProductFormOptions({ userId: user?.id, companyId: selectedCompany,
    warehouseId: selectedWarehouse, open: true });
  const refreshFormChoices = formChoices.refresh;
  const references = useMemo(() => mobileReferences(formChoices.data, selectedWarehouse), [formChoices.data, selectedWarehouse]);
  const referencesRef = useRef(references);
  referencesRef.current = references;
  const contextKey = JSON.stringify([user?.id, selectedCompany, selectedWarehouse]);
  const liveContext = useRef(contextKey);
  liveContext.current = contextKey;
  const formOpenRef = useRef(false);
  const onFormVisibilityChange = useCallback((open) => {
    formOpenRef.current = open;
    if (open) void refreshFormChoices({ force: true }).catch(() => {});
  }, [refreshFormChoices]);
  const verifyFormReferences = useCallback(async () => {
    const currentOptions = await refreshFormChoices({ force: true });
    if (liveContext.current !== contextKey || !formOpenRef.current) {
      throw new Error("Le contexte a changé. Rouvrez le formulaire avant d’enregistrer.");
    }
    return mobileReferences(currentOptions, selectedWarehouse);
  }, [contextKey, refreshFormChoices, selectedWarehouse]);

  const loadProducts = useCallback(async () => {
    if (!selectedCompany || !selectedWarehouse) {
      setProducts([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/produits`, {
        params: {
          all: true,
          fetch_all_limit: 5000,
          warehouse: selectedWarehouse,
          include_purchase_packaging_profiles: true,
        },
      });
      setProducts(
        (response.data?.products || []).map((product) =>
          mapProduct(product, referencesRef.current, selectedWarehouse)
        )
      );
    } catch (error) {
      message.error(getErrorMessage(error, "Erreur lors du chargement des produits"));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedWarehouse]);


  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") !== "new") return;
    setOpenCreateSignal(Date.now());
    params.delete("action");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!deleteProduct) return undefined;
    document.body.classList.add("eap-secure-modal-open");
    return () => document.body.classList.remove("eap-secure-modal-open");
  }, [deleteProduct]);

  const loadProduct = useCallback(
    async (id) => {
      const response = await axios.get(`${API_URL}/api/produits/${id}`, {
        params: { warehouse_id: selectedWarehouse },
      });
      return mapProduct(response.data, references, selectedWarehouse);
    },
    [references, selectedWarehouse]
  );

  const saveProduct = useCallback(
    async (form, editingId) => {
      const references = await verifyFormReferences();
      const category = findByName(references.categories, form.cat);
      const unit = findByName(references.units, form.unit);
      const warehouse = findByName(references.warehouses, form.wh);
      const tax = findByName(references.taxes, form.tax);
      if (form.tax && form.tax !== "Aucune taxe" && !tax) throw new Error("La taxe sélectionnée n’est plus disponible. Vérifiez votre choix.");

      if (!warehouse || !unit || !category) {
        throw new Error("Établissement, catégorie et unité sont requis");
      }

      const amounts = Object.fromEntries(["stock", "alert", "cost", "sale", "pkContent", "pkPrice"]
        .map((field) => [field, parseProductNumber(form[field])]));
      if (Object.values(amounts).some((value) => !Number.isFinite(value) || value < 0)) {
        throw new Error("Les quantités et les prix doivent être des nombres positifs ou nuls.");
      }

      const formData = new FormData();
      formData.append("company_id", warehouse.companyId || selectedCompany);
      formData.append("warehouse_id", warehouse.id);
      formData.append("product_type", productTypeValue(form.type));
      formData.append("name", form.name.trim());
      formData.append("slug", form.slug || genSlug(form.name));
      formData.append("barcode_symbology", form.bcSymbol || "CODE128");
      formData.append("item_code", form.code);
      formData.append("barcode_value", form.bc || form.code);
      formData.append("category_id", category.id);
      formData.append("unit_id", unit.id);
      formData.append("current_stock", amounts.stock);
      formData.append("purchase_price", amounts.cost);
      formData.append("sales_price", amounts.sale);
      formData.append("tax_id", tax?.id || "");
      formData.append("alert_quantity", amounts.alert);
      formData.append("stock_management_mode", form.stockMode || "standard");
      formData.append("description", form.desc || "");
      formData.append(
        "purchase_packaging_profiles",
        JSON.stringify(
          form.type === "Matières premières" && form.pkLabel
            ? [
                {
                  label: form.pkLabel,
                  contained_quantity: amounts.pkContent,
                  reference_purchase_price: amounts.pkPrice,
                  is_default: true,
                },
              ]
            : []
        )
      );

      if (form.imageFile instanceof File) {
        formData.append("image", form.imageFile); // → multer imageUpload.single("image")
      } else if (editingId && form.removeImage) {
        formData.append("remove_image", "true"); // → PUT met image = null
      }

      const response = editingId
        ? await axios.put(`${API_URL}/api/produits/${editingId}`, formData)
        : await axios.post(`${API_URL}/api/produits`, formData);
      const productId = editingId || response.data?.productId;
      void invalidateProducts();
      const savedProduct = await loadProduct(productId);
      await loadProducts();
      return savedProduct;
    },
    [
      loadProduct,
      loadProducts,
      invalidateProducts,
      selectedCompany,
      verifyFormReferences,
    ]
  );

  // Crée un produit parent + N déclinaisons via l'endpoint dédié. Réutilise la
  // même logique que le desktop (GestionProduit.handleSubmitVariants).
  const saveVariants = useCallback(
    async ({
      form,
      axes,
      rows,
      existingProductId = null,
      existingVariantValues = null,
    }) => {
      const references = await verifyFormReferences();
      const category = findByName(references.categories, form.cat);
      const unit = findByName(references.units, form.unit);
      const warehouse = findByName(references.warehouses, form.wh);
      const tax = findByName(references.taxes, form.tax);
      if (form.tax && form.tax !== "Aucune taxe" && !tax) throw new Error("La taxe sélectionnée n’est plus disponible. Vérifiez votre choix.");

      if (!warehouse || !unit || !category) {
        throw new Error("Établissement, catégorie et unité sont requis");
      }

      const formData = new FormData();
      formData.append("warehouse_id", warehouse.id);
      formData.append("company_id", warehouse.companyId || selectedCompany);
      formData.append("name", form.name.trim());
      formData.append("unit_id", unit.id);
      formData.append("barcode_symbology", form.bcSymbol || "CODE128");
      formData.append("stock_management_mode", form.stockMode || "standard");
      formData.append("category_id", category.id);
      formData.append("slug", form.slug || genSlug(form.name));
      formData.append("description", form.desc || "");
      formData.append("tax_id", tax?.id || "");
      // Code parent (base) : laisse le backend générer "VP-<nom>" si vide.
      if (!existingProductId && form.code) formData.append("item_code", form.code);

      formData.append("variant_attributes", JSON.stringify(buildVariantAttributes(axes)));
      if (existingProductId && existingVariantValues) {
        formData.append(
          "existing_variant_values",
          JSON.stringify(existingVariantValues)
        );
      }

      const existingKey = existingVariantValues
        ? comboKey(existingVariantValues)
        : null;

      const variantsPayload = rows.map((variant) => ({
        variant_values: variant.variant_values,
        label: variant.label,
        item_code:
          existingKey && comboKey(variant.variant_values) === existingKey
            ? undefined
            : variant.item_code
            ? String(variant.item_code).trim()
            : undefined,
        barcode_value:
          existingKey && comboKey(variant.variant_values) === existingKey
            ? undefined
            : variant.barcode_value
            ? String(variant.barcode_value).trim()
            : undefined,
        purchase_price: Number(variant.purchase_price) || 0,
        sales_price: Number(variant.sales_price) || 0,
        tax_id: variant.tax_id ?? tax?.id ?? undefined,
        opening_stock:
          existingKey && comboKey(variant.variant_values) === existingKey
            ? undefined
            : Number(variant.opening_stock) || 0,
        stock_quantitiy_alert: Number(variant.stock_quantitiy_alert) || 0,
      }));
      formData.append("variants", JSON.stringify(variantsPayload));

      if (form.imageFile instanceof File) {
        formData.append("image", form.imageFile);
      }
      rows.forEach((variant, index) => {
        if (
          variant.imageFile &&
          (!existingKey || comboKey(variant.variant_values) !== existingKey)
        ) {
          formData.append(`variant_image_${index}`, variant.imageFile);
        }
      });

      try {
        await axios.post(
          existingProductId
            ? `${API_URL}/api/produits/${existingProductId}/convert-to-variants`
            : `${API_URL}/api/produits/with-variants`,
          formData
        );
        void invalidateProducts();
      } catch (error) {
        if (error.response?.data?.code === "VARIANTS_PROFILE_NOT_ELIGIBLE") {
          throw new Error(
            "Les produits à variantes sont réservés au profil e-commerce."
          );
        }
        throw new Error(
          getErrorMessage(
            error,
            existingProductId
              ? "Erreur lors de la conversion du produit en variantes"
              : "Erreur lors de la création du produit à variantes"
          )
        );
      }
      await loadProducts();
    },
    [
      loadProducts,
      invalidateProducts,
      selectedCompany,
      verifyFormReferences,
    ]
  );

  const isProtectedProduct = useCallback(
    (product) =>
      product.backendType === "production" ||
      product.isVariantParent ||
      Number(product.mainWarehouseId) !== Number(selectedWarehouse),
    [selectedWarehouse]
  );

  const permissions = useMemo(
    () => ({
      create:
        !subscriptionWriteRestricted &&
        Boolean(selectedCompany && selectedWarehouse) &&
        hasPermission("Gestion Commerciale.Produits.Produits.create"),
      canEdit: (product) =>
        !subscriptionWriteRestricted &&
        hasPermission("Gestion Commerciale.Produits.Produits.edit") &&
        !isProtectedProduct(product),
      canDelete: (product) =>
        !subscriptionWriteRestricted &&
        hasPermission("Gestion Commerciale.Produits.Produits.delete") &&
        !isProtectedProduct(product),
      canManageStatus: (product) =>
        !subscriptionWriteRestricted &&
        (hasPermission("Gestion Commerciale.Produits.Produits.archive") ||
          hasPermission("Gestion Commerciale.Produits.Produits.disable")) &&
        !isProtectedProduct(product),
    }),
    [
      hasPermission,
      isProtectedProduct,
      selectedCompany,
      selectedWarehouse,
      subscriptionWriteRestricted,
    ]
  );

  const contextWarehouse = references.warehouses.find(
    (warehouse) => Number(warehouse.id) === Number(selectedWarehouse)
  );

  return (
    <>
      <ProductsMobile
        key={contextKey}
        products={products}
        loading={loading}
        references={references}
        onFormVisibilityChange={onFormVisibilityChange}
        referenceNotice={formChoices.error ? <Alert type="warning" showIcon
          message="Actualisation des choix impossible"
          description="Votre saisie et les choix précédents sont conservés. Réessayez avant d’enregistrer."
          action={<Button onClick={() => formChoices.refresh({ force: true }).catch(() => {})}>Réessayer</Button>} /> : null}
        permissions={permissions}
        isVariantsEligible={isVariantsEligible}
        contextLabel={contextWarehouse?.name || "Aucun établissement sélectionné"}
        openCreateSignal={openCreateSignal}
        onMenu={outletContext?.openMobileMenu}
        onScan={() => message.info("Le scanner mobile sera relié dans une prochaine étape")}
        onLoadProduct={loadProduct}
        onSave={saveProduct}
        onSaveVariants={saveVariants}
        onRequestDelete={setDeleteProduct}
        onAction={() => message.info("Impression du code-barres disponible sur desktop")}
      />

      <DeleteConfirmationModal
        visible={Boolean(deleteProduct)}
        onCancel={() => setDeleteProduct(null)}
        onConfirm={async () => {
          void invalidateProducts();
          setDeleteProduct(null);
          await loadProducts();
        }}
        entity={deleteProduct}
        entityType="produit"
        entityName={deleteProduct?.name}
        apiEndpoint={`${API_URL}/api/produits`}
      />
    </>
  );
}
